import { Icon, on_repeat_loop } from '../../components/Icon/Icon';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { ApiOrder } from '../../types';
import { Button, ButtonLink } from '../Form/Button';
import { renderTemplate } from '../utils';
import './RefillOrderBanner.css';
import { addHours, formatCalendarDate, orderIsHappening, parseApiDateTime } from './utils';

/**
 * An order a refill created, said so at the top of its own page: which refill it came from, when it
 * is due to leave the warehouse, and until when it can still be changed - with the two things that
 * can be done about it, rather than only at the foot of the page among the other order actions.
 */
export const RefillOrderBanner = ({
	order,
	canEdit,
	canCancel,
	onCancel,
}: {
	order: ApiOrder;
	canEdit: boolean;
	canCancel: boolean;
	onCancel: () => void;
}): JSX.Element => {
	const translations = useTranslation();
	const t = { ...translations.subscriptions, generic: translations.generic, urls: translations.routes };
	const { dateTimeFormat } = useFormatting();
	const { subscriptionOrder } = order;

	// the order is created, and leaves the warehouse shipInHours later - which is also the deadline
	const dispatch = addHours(parseApiDateTime(subscriptionOrder.dateCreated), Number(subscriptionOrder.shipInHours));
	/*
	 * Only an order that is still going to happen has a dispatch to announce. The time alone is not
	 * enough: an order cancelled this morning still has its old shipping window ahead of it, and
	 * saying it is due out on thursday would be a departure that is never going to take place.
	 * Otherwise the banner says which refill the order came from and nothing else - what became of
	 * it is the order's own status, which the page shows below.
	 */
	const awaitingDispatch = orderIsHappening(order.status) && dispatch.getTime() > Date.now();

	return (
		<div className="refill-order-banner">
			<Icon shape={on_repeat_loop} size={40} />
			<div className="refill-order-banner-said">
				{/* an api older than kg-api #87 does not name the refill, and the row says less */}
				<strong>
					{subscriptionOrder.listName
						? renderTemplate(t.orderFromRefill, { name: subscriptionOrder.listName })
						: t.subscriptionTitle}
				</strong>
				{awaitingDispatch && (
					<p>
						{renderTemplate(t.plannedDispatch, { date: formatCalendarDate(dispatch) })}
						{canEdit && <> · {renderTemplate(t.changeUntil, { deadline: dateTimeFormat(dispatch) })}</>}
					</p>
				)}
			</div>
			<div className="refill-order-banner-actions">
				{canEdit && (
					<ButtonLink className="small" linkTo={`${t.urls.yourOrders}/${order.id}${t.urls.edit}`}>
						{t.generic.edit}
					</ButtonLink>
				)}
				{canCancel && (
					<Button className="secondary small" onClick={onCancel}>
						{t.generic.cancel}
					</Button>
				)}
			</div>
		</div>
	);
};
